'use client'

import { useEffect, useMemo, useRef, useState } from "react"
import { LowStockProvider } from '@/components/menu/LowStockContext'
import { useTranslations } from 'next-intl'
import { AnimatePresence } from 'framer-motion'
import type { ItemTranslations } from '@/lib/menu/localizeItem'
import { FloatingNav } from './FloatingNav'
import { HeroSection } from './HeroSection'
import { AccentStrip } from './AccentStrip'
import { PromoBanner } from './PromoBanner'
import { FeaturedSection } from './FeaturedSection'
import { OrderBar } from './OrderBar'
import { ReviewsSection, type Review } from './ReviewsSection'
import { SocialLinks } from './SocialLinks'
import { MenuSection } from './MenuSection'
import { ItemModal } from './ItemModal'
import { GuidedItemFlow } from './GuidedItemFlow'
import { micrositeOpensGuidedFlow, needsRetailPicker, type RetailPickerFacts } from '@/lib/microsite/retailLine'
import { DeliverySection } from './DeliverySection'
import { DeliveryModal } from './DeliveryModal'
import { CateringSection } from "./CateringSection"
import { ReservationSection } from './ReservationSection'
import { ReservationModal } from './ReservationModal'
import { AboutSection } from './AboutSection'
import { GallerySection } from './GallerySection'
import { LocationSection } from './LocationSection'
import { Footer } from './Footer'
import { OrderSheet, type ConfirmedOrder } from './sheet/OrderSheet'
import { usePaymentReturn } from './usePaymentReturn'
import type { TableContext } from './sheet/tableContext'
import { micrositeLineSubtotal, type AppliedCoupon } from '@/lib/microsite/couponUx'
import { UpsellSheet, type UpsellSuggestion } from './UpsellSheet'
import { FulfillmentBar } from './FulfillmentBar'
import { AddressSheet } from './AddressSheet'
import { WhyDeliveryFeeSheet } from './WhyDeliveryFeeSheet'
import { shouldAcceptQuote } from './acceptQuoteGate'
import { isQuoteExpired, QUOTE_CHECKOUT_MARGIN_MS } from '@/lib/delivery/dispatchable'
import { formatDropoffLine } from '@/lib/delivery/formatDropoff'
import { Reveal } from './Reveal'
import { MicrositeTheme } from './MicrositeTheme'
import { LocaleSwitcher } from '@/components/ui/LocaleSwitcher'
import { pricingConfigFromLocation, effectiveItemCardPrice } from '@/lib/utils/pricing'
import { resolveTheme, resolveHeroVariant } from '@/lib/microsite/themes'
import type { NavStyle } from '@/lib/microsite/navStyle'
import { resolveSections, type SectionId } from '@/lib/microsite/sections'
import { zonedFactsFrom } from '@/lib/menu/zonedClientFacts'
import { useOpenStatus } from '@/lib/hours/useOpenStatus'
import { useScheduleSlots } from '@/lib/hours/useScheduleSlots'
import { useWaitEstimate } from '@/lib/microsite/useWaitEstimate'
import { fixedWait, waitForSurfaces, waitText, type LiveWaitMode, type WaitEstimate } from '@/lib/microsite/waitEstimate'
import { getOnlineOrderingPauseStatus } from '@/lib/online-orders/pauseStatus'
import { getOnlineSuggestions } from '@/app/actions/suggestionActions'
import { useOnlineSuggestionTelemetry } from '@/hooks/useOnlineSuggestionTelemetry'
import { makePublicEventSender } from '@/lib/suggestions/telemetry'
import { makeCheckoutFunnel } from '@/lib/microsite/checkoutFunnel'
import { DISPLAY_CART_MAX_IDS, ONLINE_SUGGESTIONS_LIMIT } from '@/lib/suggestions/channels'
import { redeemPrefill } from '@/app/actions/dinerReorderActions'
import { rebuildCartFromLines } from '@/lib/dinerAccount/rebuildCart'
import { formatPhone } from '@/lib/utils/phone'
import type { OperatingHours } from '@/lib/hours/openStatus'
import type { CartItem, CustomerInfo, DeliveryMode, DropoffAddress, HeldQuote, ShipQuote, ShipToAddress } from './types'
import type { ActiveCampaign } from "@/lib/donations/getActiveCampaign"
import { AttributionCapture } from './tracking/AttributionCapture'
import { cartValue, track, trackItemsFromCart, trackPurchase } from '@/lib/tracking/storefrontEvents'

// Re-export for backwards compatibility with any external imports
export type { CartItem }

type Props = {
  /** Rejilla precio×tamaño del local (`optionId|sizeOptionId` → precio), serializable. */
  sizeGrid: Record<string, number>
  /** `locations.zoned_pricing_mode`: media pizza cobra la mitad o el precio entero. */
  zonedPricingMode: string
  location: {
    id: string
    name: string
    slug: string
    /** Logo canónico del local (Settings → Negocio). Tiene prioridad sobre el legacy de microsite. */
    logo_url?: string | null
    tax_rate: number | null
    cash_discount_pct: number | null
    dual_pricing_enabled?: boolean | null
    single_price_basis?: string | null
    round_card_prices?: boolean | null
    /** PHASE E: per-transaction flat fee. */
    cash_per_transaction_fee?: number | null
    /** PHASE F: when on, the per-tx fee is charged to the card payer at checkout. */
    processing_fee_enabled?: boolean | null
    timezone: string | null
    address?: string | null
    city?: string | null
    state?: string | null
    phone?: string | null
    /** Horario de operación canónico (HoursEditor). Null = sin horario => siempre abierto. */
    operating_hours?: OperatingHours | null
    /** Minutos antes del cierre en que se dejan de tomar pedidos en línea (last call). */
    online_order_cutoff_minutes?: number | null
    /** Permite agendar pedidos para una hora futura. */
    scheduling_enabled?: boolean | null
    /** Horizonte máximo de días para agendar. */
    schedule_max_days_ahead?: number | null
    /** Botón de pánico: instante en que se pausaron las ventas online (null = no pausado). */
    online_ordering_paused_at?: string | null
    /** Instante en que la pausa se auto-levanta (próximo cierre); null = manual. */
    online_ordering_paused_until?: string | null
    /** Nota opcional del operador para la pausa. */
    online_ordering_pause_reason?: string | null
    /** Fotos del local para la sección de galería. */
    gallery_images?: string[] | null
    /** Flujo de venta elegido por el dueño ('classic' | 'guided'). Ver lib/menu/salesFlow.ts. */
    sales_flow?: string | null
  }
  microsite: Record<string, unknown> | null
  micrositeTheme?: string | null
  reviewStats?: { avg_rating: number | null; review_count: number | null } | null
  relationalReviews?: { rating: number | null; comment: string | null; customer_name: string | null }[] | null
  reservationsEnabled?: boolean
  /** `catering_settings.enabled` del local. Fail-closed en el loader. */
  cateringEnabled?: boolean
  /**
   * Delivery propio de Redy habilitado para este local (`location_delivery_config.enabled`,
   * resuelto en `loadMicrositeData`). Undefined/false ⇒ el checkout ni muestra la opción.
   * La ruta de mesa por QR NO lo pasa: un cliente sentado no pide a domicilio.
   */
  deliveryEnabled?: boolean
  /**
   * Envío por paquetería habilitado para este local (`shippingGateOpen()` +
   * `location_shipping_config.enabled` + `features.shipping`, resuelto en
   * `loadMicrositeData`). Undefined/false ⇒ el checkout ni muestra la opción.
   * La ruta de mesa por QR NO lo pasa: un cliente sentado no manda su comida
   * por paquetería.
   */
  shippingEnabled?: boolean
  /** ids de `items_shippable` para este local (I8: la vista es la única
   *  superficie del predicado de envío — nunca reimplementarlo en TS). */
  shippableItemIds?: string[]
  /** Config de depósito (card-on-file) reenviada al modal del cliente. */
  depositSettings?: {
    deposit_enabled: boolean
    deposit_amount_per_person_cents: number | null
    deposit_rules: { min_party?: number; late_cancel_hours?: number } | null
  }
  activeDonationCampaign?: ActiveCampaign | null
  donationEnabled?: boolean
  /**
   * GATE-COUPONS del local. Solo decide si el carrito DIBUJA el campo de cupón:
   * el gate de verdad lo vuelve a leer el servidor en cada llamada, así que un
   * `true` obsoleto degrada a "no encontramos ese cupón", nunca a un descuento.
   */
  couponsEnabled?: boolean
  /** Checkout por pasos (`features.checkout_steps_v2`). */
  checkoutStepsV2?: boolean
  /** Barra superior sólida: `dark` (default) o `light` (logo oscuro). */
  navStyle?: NavStyle
  /** Sustituciones (spec sustituciones §4.2): el cliente puede elegir un plan B
   *  por línea o para la orden entera. En modo mesa (QR) se apaga siempre —
   *  ver el `&& !tableContext` al pasarlo a `OrderSheet`. */
  substitutionsEnabled?: boolean
  /** Sugerencias del motor en la hoja de "¡Agregado!". Default true (lo que la web siempre hizo). */
  suggestionsEnabled?: boolean
  /** % de la oferta de bienvenida por aceptar SMS de promociones; null = sin oferta. */
  welcomePct?: number | null
  /**
   * Código que venía en `?coupon=` (el enlace y el QR que reparte el panel de
   * cupones). Se aplica solo al abrir el carrito con algo dentro — no antes: un
   * carrito vacío no llega a ningún mínimo y el cupón se rechazaría a sí mismo
   * antes de que el cliente eligiera su primer plato.
   */
  initialCouponCode?: string | null
  /**
   * Token `prefill` de `?prefill=` (reorder desde /cuenta). Se canjea UNA vez al
   * montar: rellena nombre/teléfono/correo y reconstruye el carrito contra el
   * menú actual. Un refresh no lo re-canjea (se limpia de la URL).
   */
  initialPrefillToken?: string | null
  /** Modo mesa (pedido por QR): presente ⇒ sheet sin pickup y POST con qr_token. */
  tableContext?: TableContext | null
  /** Medición de anuncios activa en este local (hay pixel): el pie muestra la
   *  exclusión «Your Privacy Choices». El pixel lo carga la página, no este componente. */
  adTrackingActive?: boolean
  /** Tiempo de espera que calculó el servidor (PR K). Sin él (p. ej. la página de
   *  mesa) es el fijo de `pickup_wait_minutes`, como siempre. */
  initialWaitEstimate?: WaitEstimate
  /** Dónde se muestra el tiempo real (`features.live_wait_time`): solo en la
   *  confirmación o en toda la tienda. Ausente = apagado. */
  liveWaitMode?: LiveWaitMode
  categories: Array<{
    id: string
    name: string
    color: string | null
    sort_order: number | null
  }>
  items: Array<{
    id: string
    name: string
    description: string | null
    translations?: ItemTranslations | null
    price: number
    image_url: string | null
    image_focal?: string | null
    is_available: boolean | null
    category_id: string | null
    is_display_featured?: boolean | null
    /** Ventana de venta programada (día/hora). null/vacío = siempre disponible. */
    available_from?: string | null
    available_until?: string | null
    available_days?: number[] | null
    /** Pizza armable por zonas (mitad/mitad). Solo el ItemModal legado la soporta —
     *  ver `usesGuidedFlow` más abajo. */
    zoned_build?: boolean | null
    item_modifier_groups?: Array<{
      /** Posición del grupo DENTRO de este ítem (definida por el dueño). Sin
       *  ella, buildGuidedSteps cae al orden sin garantías de PostgREST. */
      sort_order?: number | null
      modifier_groups: {
        id: string
        name: string
        required: boolean | null
        min_selections: number | null
        max_selections: number | null
        /** 'normal' | 'removal' | 'allergy_alert'. La lee GuidedItemFlow (vía
         *  classifyOption) para decidir Agregar/Quitar/pregunta — sin ella,
         *  TS no avisaría si algún día la consulta deja de traerla. */
        display_style?: string | null
        modifier_options: Array<{
          id: string
          name: string
          price_delta: number
          is_available: boolean | null
        }>
      } | null
    }>
    /** Súper online (I2): solo llega con online_retail_v2. Ausente = el carrito grava
     *  todo el subtotal, como main. */
    is_taxable?: boolean | null
    /** Súper online: weight_unit / variants solo llegan con online_retail_v2. */
  } & RetailPickerFacts>
}

/** Un ítem del menú tal como los trae este componente — lo reusan `OrderSheet`
 *  y `substitutionCandidates` (spec sustituciones §4.2) para no redeclarar la
 *  misma forma anidada. */
export type MicrositeItem = Props['items'][number]

// ─── Component ────────────────────────────────────────────────────────────────

export function RestaurantMicrosite({ sizeGrid, zonedPricingMode, location, microsite, micrositeTheme, categories, items, reviewStats, relationalReviews, reservationsEnabled, cateringEnabled, deliveryEnabled, shippingEnabled, shippableItemIds, depositSettings, activeDonationCampaign, donationEnabled, couponsEnabled, navStyle = 'dark', checkoutStepsV2 = false, substitutionsEnabled, suggestionsEnabled = true, welcomePct = null, initialCouponCode, initialPrefillToken, tableContext, adTrackingActive = false, initialWaitEstimate, liveWaitMode = 'off' }: Props) {
  const t = useTranslations('public.microsite')
  const [cart, setCart] = useState<CartItem[]>([])
  // Telemetría de la hoja de sugerencias (PR 5c): impresión al resolver, tap al
  // tocar, enlace sesión → pedido al confirmar. Nunca lanza.
  const suggestionTelemetry = useOnlineSuggestionTelemetry({ locationId: location.id, enabled: suggestionsEnabled })
  // Delivery (Fase 4): vive acá, NO en OrderSheet — el sheet se desmonta al cerrarlo
  // (AnimatePresence) y el cliente perdería dirección y cotización al ir a agregar algo más.
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("pickup")
  const [dropoff, setDropoff] = useState<DropoffAddress | null>(null)
  const [heldQuote, setHeldQuote] = useState<HeldQuote | null>(null)
  const [addressSheetOpen, setAddressSheetOpen] = useState(false)
  // "¿Por qué cobramos envío?" (spec 2026-09-03): un solo dueño, un solo sheet.
  const [whyFeeOpen, setWhyFeeOpen] = useState(false)
  // Espejos en ref para el cerrojo de onQuote: la respuesta llega tras un await y
  // el closure del render que la disparó puede tener un modo viejo.
  const deliveryModeRef = useRef<DeliveryMode>('pickup')
  const addressSheetOpenRef = useRef(false)
  deliveryModeRef.current = deliveryMode
  addressSheetOpenRef.current = addressSheetOpen

  /**
   * ÚNICO lugar que decide qué se limpia al cambiar de modo (spec 2026-09-03 §4.2,
   * P1-4). Lo usan la barra y el OrderSheet; el sheet conserva solo sus efectos de
   * navegación. "pickup" limpia dirección y cotización de delivery Y de paquetería.
   */
  function changeDeliveryMode(mode: DeliveryMode) {
    setDeliveryMode(mode)
    if (mode === 'pickup') {
      setDropoff(null)
      setHeldQuote(null)
      setShipTo(null)
      setShipQuote(null)
      setShipQuoteCartFingerprint(null)
    }
  }

  /** La barra pidió un modo. Domicilio sin cotización viva ⇒ abrir el mini-sheet. */
  const [fulfillmentChosenInBar, setFulfillmentChosenInBar] = useState(false)
  function handleBarChangeMode(mode: 'pickup' | 'delivery') {
    changeDeliveryMode(mode)
    // El cliente ya contestó "¿Cómo lo recibes?" en la carta: el checkout por
    // pasos no se lo vuelve a preguntar (spec 2026-10-19 §5.2).
    setFulfillmentChosenInBar(true)
    if (mode === 'delivery') {
      // Una cotización sin dirección nunca sirve para pagar: el setter de
      // onQuote puede resolver DESPUÉS de que "Recojo yo" ya limpió `dropoff`.
      // eslint-disable-next-line react-hooks/purity
      const fresh = !!dropoff && !!heldQuote && !isQuoteExpired(heldQuote, Date.now(), QUOTE_CHECKOUT_MARGIN_MS)
      if (!fresh) setAddressSheetOpen(true)
    }
  }
  // Envío (Fase 1b T11): mismo motivo — vive acá para sobrevivir al desmontaje
  // del sheet. `shipQuoteCartFingerprint` viaja EMPAREJADO con `shipQuote` (no
  // dentro del tipo `ShipQuote` — ese es el contrato server-trusted de T8/T11,
  // esto es puramente client-side) para que la invalidación por cambio de
  // carrito sobreviva también a un cierre/reapertura del sheet.
  const [shipTo, setShipTo] = useState<ShipToAddress | null>(null)
  const [shipQuote, setShipQuote] = useState<ShipQuote | null>(null)
  const [shipQuoteCartFingerprint, setShipQuoteCartFingerprint] = useState<string | null>(null)
  // Cupón: vive acá por el mismo motivo que la cotización de envío — el sheet se
  // desmonta al cerrarlo, y el cliente que vuelve al menú por un postre no debe
  // perder el descuento que ya aplicó (ni tener que volver a teclearlo).
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
  // El código del deep link, PENDIENTE de aplicar. Se consume una sola vez (al
  // primer intento, salga bien o mal): si se reintentara en cada apertura del
  // carrito, un código vencido volvería a gritar su error cada vez que el
  // cliente abre el carrito, y quitarlo a mano no serviría de nada.
  const [pendingCouponCode, setPendingCouponCode] = useState<string | null>(
    initialCouponCode?.trim() || null,
  )
  const [prefillCustomer, setPrefillCustomer] = useState<Partial<CustomerInfo> | null>(null)
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null)
  // Task 11: true exactamente cuando el carrito EN CURSO nació de "Volver a
  // pedir" (se pobló con líneas reconstruidas del prefill, no a mano). Viaja
  // como prop hasta PaymentStep.submitOrder, que lo manda al POST solo en ese
  // caso — es telemetría (reorder_complete), nunca gobierna precio ni tenencia.
  const [cartFromPrefill, setCartFromPrefill] = useState(false)
  const [orderSheetOpen, setOrderSheetOpen] = useState(false)
  // Popup con los links a marketplaces (DoorDash/UberEats/Grubhub). Ojo: esto NO
  // es el delivery propio de Redy; de ahí el nombre `marketplace*`.
  const [marketplaceOpen, setMarketplaceOpen] = useState(false)
  const [upsellOpen, setUpsellOpen] = useState(false)
  const [reservationOpen, setReservationOpen] = useState(false)
  const [upsellData, setUpsellData] = useState<{
    addedItem: CartItem
    /** null = sugerencias apagadas por el dueño (suggestionsEnabled=false). */
    suggestions: UpsellSuggestion[] | null
    loading: boolean
  } | null>(null)
  // Contador de peticiones al motor: si el cliente agrega dos cosas seguidas,
  // la respuesta vieja no pisa a la nueva.
  const upsellRequestRef = useRef(0)
  // Ítem de una sugerencia del upsell que trae modificadores: se resuelve en el
  // modal (encima del sheet), no se agrega a ciegas.
  const [upsellModifierItem, setUpsellModifierItem] = useState<Props['items'][number] | null>(null)

  // Vacío/ausente = sin color propio del merchant ⇒ MicrositeTheme cae al acento
  // diseñado de cada tema. No poner un default de marca acá: teñiría los 15.
  const accentColor = (microsite?.accent_color as string | undefined) ?? ''
  // Pareja tipográfica del merchant (columna nueva: puede no venir). Viaja
  // dentro del objeto microsite igual que accent_color, así que la ruta de mesa
  // por QR y el flag QA ?previewFont la heredan sin plomería extra.
  const fontPairId = (microsite?.font_pair as string | undefined) ?? ''
  // Intensidad del velo del hero. null/ausente = la que diseñó el tema, así que
  // una página sin elección se ve EXACTAMENTE igual que antes de esta columna.
  const heroOverlayLevel = (microsite?.hero_overlay_level as number | null | undefined) ?? null
  const themeTokens = resolveTheme(micrositeTheme).tokens
  // Composición del hero: la del tema salvo que el merchant haya elegido una de
  // las compartidas. resolveHeroVariant rechaza el hero de OTRO tema, que traería
  // su color de firma a esta paleta.
  const heroVariant = resolveHeroVariant(
    micrositeTheme,
    microsite?.hero_variant as string | undefined,
  )
  const pricingCfg = pricingConfigFromLocation(location)

  // «Dominio propio»: el cliente pagó (o canceló) en useredy.com y vuelve aquí.
  // Con pago, el sheet abre directo en la confirmación; con cancelación, recupera
  // su carrito. Ver lib/microsite/hostedPayment.ts.
  const [returnedOrder, setReturnedOrder] = useState<ConfirmedOrder | null>(null)
  // Vuelta del pago en otro host (PR 5c): la sesión de sugerencias de la carga
  // anterior murió con la página; su id viajó en el stash. Un solo evento
  // `order` con ese id, directo al sender público. Best-effort, jamás lanza.
  function linkHostedOrder(sessionId: string | undefined, orderId: string): void {
    if (!sessionId) return
    try {
      makePublicEventSender({ locationId: location.id })({ session_id: sessionId, events: [{ event: 'order', order_id: orderId }] })
    } catch {
      // telemetría
    }
  }
  const { notice: paymentReturnNotice } = usePaymentReturn(location.slug, {
    restoreCart: (restored) => {
      setCart(restored)
      setOrderSheetOpen(true)
    },
    showConfirmation: (order, stash) => {
      setReturnedOrder(order)
      setOrderSheetOpen(true)
      linkHostedOrder(stash?.suggestionSessionId, order.id)
      // Embudo del checkout: el pago fue en otro host; el `paid` va con la sesión del stash.
      if (stash?.funnel?.sessionId) {
        const fn = stash.funnel
        makeCheckoutFunnel({ locationId: location.id, path: fn.path, newId: () => fn.sessionId })
          .paid({ fulfillment: fn.fulfillment, scheduled: fn.scheduled, orderId: order.id, displayedTotal: fn.chargedTotal })
      }
      // Purchase del navegador al volver del pago de paso (la página /pagar no
      // carga el pixel del dueño). Valor del servidor; una vez por orden.
      trackPurchase({ orderId: order.id, conversion: order.conversion, items: trackItemsFromCart(stash?.cart ?? [], pricingCfg) })
    },
  })

  // Reorder (spec §7.4): canje único del token de prefill al montar. El guard por
  // ref sobrevive al doble montaje de StrictMode en dev: sin él, la primera llamada
  // consumía el token y la segunda recibía "used" — y el prefill no hacía nada justo
  // en desarrollo.
  const prefillRedeemedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialPrefillToken || prefillRedeemedRef.current === initialPrefillToken) return
    prefillRedeemedRef.current = initialPrefillToken
    ;(async () => {
      const res = await redeemPrefill(initialPrefillToken)
      // El token ya se consumió (haya salido bien o mal): fuera de la URL para
      // que un refresh no muestre un error por "token usado".
      window.history.replaceState(null, '', window.location.pathname)
      if (!res.ok) {
        setPrefillNotice(t('reorderUnavailable'))
        return
      }
      const { cart: rebuilt, skipped } = rebuildCartFromLines(res.lines, items, pricingCfg, location.timezone ?? 'UTC')
      setPrefillCustomer({
        name: res.customer.name,
        phone: res.customer.phone ? formatPhone(res.customer.phone) : '',
        email: res.customer.email,
      })
      if (rebuilt.length > 0) {
        setCart(rebuilt)
        setCartFromPrefill(true)
        setOrderSheetOpen(true)
      }
      if (skipped.length > 0) setPrefillNotice(t('reorderSkipped', { count: skipped.length }))
    })()
    // Solo al montar: el token es de esta visita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Task 11 (fix ronda 2): un carrito vacío YA NO es el que reconstruyó el
  // prefill — sea porque el pedido se completó (OrderSheet.onSuccess hace
  // setCart([]) tras el éxito) o porque el cliente lo vació a mano (CartStep:
  // removeItem/updateQuantity hasta 0). Sin este reset, `cartFromPrefill`
  // seguía en `true` el resto de la visita: un cliente que arma un SEGUNDO
  // pedido a mano, en la misma sesión, tras uno reordenado, marcaría también
  // ese segundo pedido como reorder_complete.
  useEffect(() => {
    if (cart.length === 0 && cartFromPrefill) setCartFromPrefill(false)
  }, [cart, cartFromPrefill])

  // Un Map no cruza el límite servidor→cliente, así que la rejilla llega como objeto
  // plano y se convierte UNA vez acá, no en cada modal que se abre.
  const zonedFacts = useMemo(() => zonedFactsFrom(sizeGrid, zonedPricingMode), [sizeGrid, zonedPricingMode])

  // Normalizado a `string | null`: MenuSection/FeaturedSection ahora exigen la
  // prop (ya no es opcional — ver salesFlow.ts), y `usesGuidedFlow` trata
  // undefined y null igual, así que esto no cambia el comportamiento.
  const salesFlow = location.sales_flow ?? null

  function itemUsesGuidedFlow(item: Props['items'][number]): boolean {
    // Lo retail (peso / variantes, solo con online_retail_v2) va siempre al modal
    // clásico; para un plato es `usesGuidedFlow`, como en main.
    return micrositeOpensGuidedFlow(salesFlow, item)
  }

  // Fotos de los platillos del menú para el carrusel de "Sobre nosotros".
  // Reusa items.image_url (ya existen) — sin trabajo extra para el operador.
  const foodPhotos = items
    .filter((i) => !!i.image_url)
    .slice(0, 12)
    .map((i) => ({ url: i.image_url as string, name: i.name }))

  // Estado de apertura (reactivo, recalcula cada minuto). null hasta el mount
  // => fail-open: mientras es desconocido NO se bloquea el pedido.
  const openStatus = useOpenStatus(
    location.operating_hours ?? null,
    location.timezone,
    location.online_order_cutoff_minutes ?? 0,
  )
  // Botón de pánico: pausa temporal de ventas online (cierre de emergencia).
  // Se recalcula en cada tick de useOpenStatus (por minuto), así el auto-reanudar
  // al próximo cierre se refleja solo. Bloquea el checkout ANTES que el horario.
  const orderingPaused = getOnlineOrderingPauseStatus(location, new Date()).paused
  const acceptingOrders = (openStatus?.isAcceptingOrders ?? true) && !orderingPaused

  // Tiempo de espera. Fijo = `pickup_wait_minutes` (default 20), como siempre.
  // Con `live_wait_time` lo calcula la cocina y se refresca solo:
  //  - "everywhere": UN valor para hero, barra, carrito, ¿Cuándo?, primer
  //    horario y confirmación;
  //  - "confirmation": la tienda sigue con el fijo y el real aparece recién en
  //    la confirmación (después de pagar).
  const fixedStoreWait = fixedWait(microsite?.pickup_wait_minutes as number | null | undefined)
  const liveWait = useWaitEstimate(
    initialWaitEstimate ?? fixedStoreWait,
    location.slug,
    liveWaitMode !== 'off' && initialWaitEstimate?.mode === 'live',
  )
  const { store: waitEstimate, confirm: confirmWaitEstimate } = waitForSurfaces(liveWaitMode, liveWait, fixedStoreWait)

  // Pedidos agendados: slots derivados del horario. lead = el tiempo de espera
  // vigente. Vacío si el agendado no está habilitado.
  const scheduleDays = useScheduleSlots(location.operating_hours ?? null, location.timezone, {
    enabled: location.scheduling_enabled ?? false,
    leadMinutes: waitEstimate.minutes,
    maxDaysAhead: location.schedule_max_days_ahead ?? 7,
  })

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0)
  // Misma fórmula que el subtotal del OrderSheet (micrositeLineSubtotal): base a
  // precio tarjeta + los deltas de los modificadores (que NUNCA se redondean).
  // Sin los deltas, el "Ver carrito · $X" del upsell mostraba menos que el carrito.
  const cartTotal = cart.reduce((s, i) => s + micrositeLineSubtotal(i, pricingCfg), 0)
  // Comida del carrito en centavos para el explicador. Misma fórmula que el
  // subtotal del OrderSheet (cartTotal); 0 = sin carrito → el sheet usa su ejemplo.
  const whyFeeFoodCents = Math.round(cartTotal * 100)

  function hasModifierGroups(item: Props['items'][number]): boolean {
    return !!item.item_modifier_groups?.some((g) => g.modifier_groups != null)
  }

  function handleAddToCart(item: CartItem) {
    const newCart = [...cart, item]
    setCart(newCart)
    // Medición de anuncios (no-op sin pixel). Embudo único: carta, destacados y upsell.
    const added = trackItemsFromCart([item], pricingCfg)
    track('add_to_cart', { value: cartValue(added), currency: 'USD', items: added })
    if (!suggestionsEnabled) {
      setUpsellData({ addedItem: item, suggestions: null, loading: false })
      setUpsellOpen(true)
      return
    }
    // La hoja se abre YA con esqueleto; el motor (server action, canal online)
    // responde en lo que tardan sus consultas. Devuelve ids+motivo y se
    // resuelve contra `items`, que es lo que la hoja sabe pintar.
    const requestId = ++upsellRequestRef.current
    setUpsellData({ addedItem: item, suggestions: [], loading: true })
    setUpsellOpen(true)
    const cartItemIds = Array.from(new Set(newCart.map((c) => c.itemId)))
    getOnlineSuggestions({ locationId: location.id, cartItemIds: cartItemIds.slice(0, DISPLAY_CART_MAX_IDS), limit: ONLINE_SUGGESTIONS_LIMIT })
      .then((rows) => {
        if (requestId !== upsellRequestRef.current) return
        // null = el dueño apagó las sugerencias: la hoja se queda sin la fila (≠ "sin resultados").
        if (rows === null) {
          setUpsellData((d) => (d ? { ...d, suggestions: null, loading: false } : d))
          return
        }
        const byId = new Map(items.map((i) => [i.id, i]))
        const resolved: UpsellSuggestion[] = []
        for (const r of rows) {
          const full = byId.get(r.id)
          if (full) resolved.push({ item: full, reason: r.reason, score: r.score })
        }
        resolved.forEach((s, index) => suggestionTelemetry?.impression({ item: { id: s.item.id }, reason: s.reason, score: s.score }, index))
        setUpsellData((d) => (d ? { ...d, suggestions: resolved, loading: false } : d))
      })
      .catch(() => {
        if (requestId !== upsellRequestRef.current) return
        setUpsellData((d) => (d ? { ...d, suggestions: [], loading: false } : d))
      })
    // Do NOT auto-open the order sheet; let UpsellSheet handle that.
  }

  function handleOpenOrder() {
    setOrderSheetOpen(true)
  }

  // CTA principal del hero: lleva a la sección del menú (#menu) en vez de abrir
  // el carrito vacío.
  function handleViewMenu() {
    if (typeof document !== 'undefined') {
      document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Derived microsite fields
  const businessName = location.name
  const tagline = microsite?.tagline as string | null | undefined
  const heroImageUrl = microsite?.hero_image_url as string | null | undefined
  const heroVideoUrl = microsite?.hero_video_url as string | null | undefined
  // Logo canónico (locations.logo_url, editado en Settings → Negocio) con
  // fallback al campo legacy de location_microsite. Se muestra en la barra y el hero.
  const logoUrl = location.logo_url ?? (microsite?.logo_url as string | null | undefined)
  const doordashUrl = microsite?.doordash_url as string | null | undefined
  const uberEatsUrl = (microsite?.ubereats_url ?? microsite?.uber_eats_url) as
    | string
    | null
    | undefined
  const grubhubUrl = microsite?.grubhub_url as string | null | undefined
  // Hay links de marketplace si al menos una URL no está vacía.
  const hasMarketplaceLinks = [doordashUrl, uberEatsUrl, grubhubUrl].some(
    (u) => !!u && u.trim().length > 0,
  )
  // ¿Redy le puede llevar la comida a ESTE cliente, en ESTA página? `deliveryEnabled`
  // ya viene resuelto por el servidor (allowlist + motor del local), pero el
  // OrderSheet además apaga delivery en modo mesa — el cliente ya está sentado. Se
  // repite ese `!tableContext` acá a propósito: si no, en modo mesa esconderíamos
  // los links de marketplace sin darle al cliente ninguna alternativa a cambio.
  const redyDeliveryLive = Boolean(deliveryEnabled) && !tableContext
  // Con delivery propio vivo NO mandamos al cliente a un marketplace: se lleva 25 %
  // y la relación que el merchant ya tenía. Esto apaga las tres superficies a la vez
  // (botón del header, botón del hero + su popup, y la sección de links) — dejar una
  // sola prendida sería la misma fuga con otro nombre.
  const showMarketplaceLinks = hasMarketplaceLinks && !redyDeliveryLive
  const showAbout = microsite?.show_about as boolean | null | undefined
  const aboutText = microsite?.about_text as string | null | undefined
  const aboutPhotoUrl = microsite?.about_photo_url as string | null | undefined
  const pickupWaitMinutes = waitEstimate.minutes
  const mapsUrl = (microsite?.google_maps_url as string | null | undefined) ?? undefined
  // Fase 3 — contenido del merchant (cada sección degrada si su dato está vacío)
  const promoText = microsite?.promo_text as string | null | undefined
  const promoCtaUrl = microsite?.promo_cta_url as string | null | undefined
  const instagramUrl = microsite?.instagram_url as string | null | undefined
  const facebookUrl = microsite?.facebook_url as string | null | undefined
  const tiktokUrl = microsite?.tiktok_url as string | null | undefined
  const reviews = (microsite?.reviews as Review[] | null | undefined) ?? []

  // Orden y encendido elegidos por el merchant. `sections` es jsonb, así que
  // resolveSections normaliza cualquier cosa que haya quedado guardada.
  const sectionPrefs = resolveSections(microsite?.sections)

  // Registro id → sección. El `null` significa "este comercio no tiene esto"
  // (sin reservas, sin delivery, sin texto de nosotros): la preferencia del
  // merchant no puede fabricar una sección sin datos.
  const sectionNodes: Record<SectionId, React.ReactNode> = {
    promo: (
      <Reveal>
        <PromoBanner text={promoText} ctaUrl={promoCtaUrl} />
      </Reveal>
    ),
    featured: (
      <Reveal>
        <FeaturedSection
          items={items}
          pricingCfg={pricingCfg}
          zonedFacts={zonedFacts}
          accentColor={accentColor}
          timezone={location.timezone}
          onAddToCart={handleAddToCart}
          salesFlow={salesFlow}
          locationId={location.id}
        />
      </Reveal>
    ),
    menu: (
      <Reveal>
        {redyDeliveryLive && deliveryMode !== 'ship' && (
          <FulfillmentBar
            mode={deliveryMode === 'delivery' ? 'delivery' : 'pickup'}
            pickupWaitMinutes={pickupWaitMinutes}
            waitEstimate={waitEstimate}
            dropoffLine={dropoff ? formatDropoffLine(dropoff) : null}
            quote={heldQuote}
            accentColor={accentColor}
            onChangeMode={handleBarChangeMode}
            onEditAddress={() => setAddressSheetOpen(true)}
            onWhyFee={() => setWhyFeeOpen(true)}
          />
        )}
        <MenuSection
          categories={categories}
          items={items}
          pricingCfg={pricingCfg}
          zonedFacts={zonedFacts}
          accentColor={accentColor}
          timezone={location.timezone}
          onAddToCart={handleAddToCart}
          salesFlow={salesFlow}
          locationId={location.id}
        />
      </Reveal>
    ),
    reservations: reservationsEnabled ? (
      <Reveal delay={60}>
        <ReservationSection onOpen={() => setReservationOpen(true)} accentColor={accentColor} />
      </Reveal>
    ) : null,
    catering: cateringEnabled ? (
      <Reveal delay={60}>
        <CateringSection slug={location.slug ?? ""} />
      </Reveal>
    ) : null,
    delivery:
      doordashUrl || uberEatsUrl || grubhubUrl ? (
        <Reveal delay={60}>
          <DeliverySection
            doordashUrl={doordashUrl ?? undefined}
            uberEatsUrl={uberEatsUrl ?? undefined}
            grubhubUrl={grubhubUrl ?? undefined}
            redyDeliveryEnabled={redyDeliveryLive}
          />
        </Reveal>
      ) : null,
    about:
      showAbout && aboutText ? (
        <Reveal delay={60}>
          <AboutSection
            text={aboutText}
            photoUrl={aboutPhotoUrl ?? undefined}
            foodPhotos={foodPhotos}
          />
        </Reveal>
      ) : null,
    gallery: location.gallery_images?.length ? (
      <Reveal delay={60}>
        <GallerySection images={location.gallery_images} />
      </Reveal>
    ) : null,
    reviews: (
      <Reveal delay={60}>
        <ReviewsSection
          reviews={
            (relationalReviews ?? []).length > 0
              ? (relationalReviews ?? []).map((r) => ({
                  author: r.customer_name ?? undefined,
                  text: r.comment ?? undefined,
                  rating: r.rating ?? undefined,
                }))
              : reviews
          }
          avgRating={reviewStats?.avg_rating ?? null}
          reviewCount={reviewStats?.review_count ?? null}
        />
      </Reveal>
    ),
    location: (
      <Reveal delay={60}>
        <LocationSection location={location} />
      </Reveal>
    ),
    social:
      instagramUrl || facebookUrl || tiktokUrl ? (
        <div className="flex justify-center py-10" style={{ background: 'var(--ms-bg)' }}>
          <SocialLinks instagram={instagramUrl} facebook={facebookUrl} tiktok={tiktokUrl} />
        </div>
      ) : null,
  }

  return (
    // «¡Quedan pocas!» (components/menu/LowStockContext): lo leen las tarjetas de
    // la carta, los destacados y los selectores de sabor.
    <LowStockProvider locationId={location.id}>
    <MicrositeTheme
      themeId={micrositeTheme}
      accentColor={accentColor}
      fontPairId={fontPairId}
      heroOverlayLevel={heroOverlayLevel}
    >
      <div
        className="min-h-screen"
        // paddingBottom: la barra «Ver mi pedido» (fixed) no tapa el pie de la página.
        style={{ background: 'var(--ms-bg)', color: 'var(--ms-text)', paddingBottom: 'var(--ms-order-bar-h, 0px)' }}
      >
      <OrderBar
        count={cartCount}
        total={cartTotal}
        accentColor={accentColor}
        hidden={orderSheetOpen || upsellOpen}
        onOpen={handleOpenOrder}
      />
      {/* De dónde llegó el cliente (utm_*, fbclid…). En la mesa no: ya está en el local. */}
      {!tableContext && <AttributionCapture />}
      <FloatingNav
        businessName={businessName}
        cartCount={cartCount}
        logoUrl={logoUrl ?? undefined}
        onOrder={handleOpenOrder}
        navStyle={navStyle}
        hasDelivery={showMarketplaceLinks}
        onDelivery={() => setMarketplaceOpen(true)}
        localeSwitcher={
          <LocaleSwitcher
            variant="toggle"
            className={`text-sm font-semibold px-2 py-1 rounded-full transition ${
              navStyle === 'light' ? 'bg-black/5 text-gray-900 hover:bg-black/10' : 'bg-white/15 text-white hover:bg-white/25'
            }`}
          />
        }
      />

      <HeroSection
        businessName={businessName}
        tagline={tagline ?? undefined}
        heroImageUrl={heroImageUrl ?? undefined}
        heroVideoUrl={heroVideoUrl ?? undefined}
        logoUrl={logoUrl ?? undefined}
        onViewMenu={handleViewMenu}
        onDelivery={() => setMarketplaceOpen(true)}
        hasDelivery={showMarketplaceLinks}
        openStatus={openStatus}
        heroVariant={heroVariant}
        city={location.city}
        pickupWaitMinutes={pickupWaitMinutes}
        readyInLabel={waitText(waitEstimate, 'readyIn', t)}
      />

      <AccentStrip />

      {prefillNotice && !orderSheetOpen && (
        <div role="status" className="mx-auto max-w-6xl px-5 pt-4 text-sm" style={{ color: 'var(--redy-secondary)' }}>
          {prefillNotice}
        </div>
      )}

      {paymentReturnNotice && !orderSheetOpen && (
        <div role="status" aria-live="polite" className="mx-auto max-w-6xl px-5 pt-4 text-sm font-medium" style={{ color: 'var(--redy-charcoal)' }}>
          {paymentReturnNotice}
        </div>
      )}

      {/* Secciones en el orden que eligió el merchant (o el de siempre si no
          eligió). Cada una conserva SU condición funcional: apagar "delivery"
          desde el panel la esconde, pero encenderla no inventa un delivery que
          el comercio no tiene — por eso son un AND, no un reemplazo. */}
      {sectionPrefs.map(({ id, enabled }) => {
        if (!enabled) return null
        const node = sectionNodes[id]
        return node ? <div key={id}>{node}</div> : null
      })}

      <Footer
        businessName={businessName}
        slug={location.slug}
        address={location.address ?? null}
        city={location.city ?? null}
        state={location.state ?? null}
        phone={(microsite?.support_phone as string) ?? null}
        email={(microsite?.support_email as string) ?? null}
        privacyChoices={adTrackingActive && !tableContext}
      />

      {reservationsEnabled && (
        <ReservationModal
          open={reservationOpen}
          onClose={() => setReservationOpen(false)}
          slug={location.slug}
          accentColor={accentColor}
          tz={location.timezone ?? 'America/Chicago'}
          depositSettings={depositSettings}
        />
      )}

      <AnimatePresence>
        {upsellOpen && upsellData && (
          <UpsellSheet
            key="upsell"
            addedItem={{
              name: upsellData.addedItem.name,
              price: upsellData.addedItem.price,
              // CartItem no trae traducciones: se toman del menú por itemId (solo display).
              translations: items.find((i) => i.id === upsellData.addedItem.itemId)?.translations ?? null,
            }}
            suggestions={upsellData.suggestions}
            loading={upsellData.loading}
            cartTotal={cartTotal}
            cartCount={cart.length}
            accentColor={accentColor}
            pricingCfg={pricingCfg}
            onAddSuggestion={(sugg, index) => {
              suggestionTelemetry?.tap({ item: { id: sugg.item.id }, reason: sugg.reason, score: sugg.score }, index)
              // Las sugerencias son ítems completos del menú: si traen grupos de
              // modificadores (incl. obligatorios tipo "elige proteína") hay que
              // abrir el modal, igual que al tocar el ítem en el menú. Agregar a
              // ciegas mandaba a cocina un ítem sin sus opciones y cobraba el
              // precio base sin los deltas.
              const fullItem = sugg.item
              // Súper online (N6): un producto por peso o con variantes se elige en el
              // modal, con el ítem de la carta (el que trae weight_unit / variants). Sin
              // online_retail_v2 ninguna fila las trae y esto nunca se cumple.
              const menuItem = items.find((i) => i.id === fullItem.id)
              if (menuItem && needsRetailPicker(menuItem)) {
                setUpsellModifierItem(menuItem)
                return
              }
              if (hasModifierGroups(fullItem)) {
                setUpsellModifierItem(fullItem)
                return
              }
              const cardPrice = effectiveItemCardPrice(sugg.item.price, pricingCfg)
              handleAddToCart({
                cartId: `${sugg.item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                itemId: sugg.item.id,
                name: sugg.item.name,
                price: cardPrice,
                quantity: 1,
                modifiers: [],
              })
            }}
            onContinueShopping={() => {
              upsellRequestRef.current++
              setUpsellOpen(false)
              setUpsellModifierItem(null)
            }}
            onViewCart={() => {
              upsellRequestRef.current++
              setUpsellOpen(false)
              setOrderSheetOpen(true)
            }}
          />
        )}
        {upsellModifierItem && (
          itemUsesGuidedFlow(upsellModifierItem) ? (
            <GuidedItemFlow
              key="upsell-modifiers"
              item={upsellModifierItem}
              pricingCfg={pricingCfg}
              accentColor={accentColor}
              onAdd={(cartItem) => {
                setUpsellModifierItem(null)
                // Reabre el upsell con sugerencias frescas (ya sin este ítem).
                handleAddToCart(cartItem)
              }}
              onClose={() => setUpsellModifierItem(null)}
            />
          ) : (
            <ItemModal
              key="upsell-modifiers"
              item={upsellModifierItem}
              pricingCfg={pricingCfg}
              zonedFacts={zonedFacts}
              accentColor={accentColor}
              locationId={location.id}
              onAdd={(cartItem) => {
                setUpsellModifierItem(null)
                // Reabre el upsell con sugerencias frescas (ya sin este ítem).
                handleAddToCart(cartItem)
              }}
              onClose={() => setUpsellModifierItem(null)}
            />
          )
        )}
        {orderSheetOpen && (
          <OrderSheet
            key="order"
            location={location}
            cart={cart}
            activeDonationCampaign={activeDonationCampaign}
            donationEnabled={donationEnabled}
            setCart={setCart}
            pricingCfg={pricingCfg}
            accentColor={accentColor}
            pickupWaitMinutes={pickupWaitMinutes}
            waitEstimate={waitEstimate}
            confirmWaitEstimate={confirmWaitEstimate}
            mapsUrl={mapsUrl}
            acceptingOrders={acceptingOrders}
            openStatus={openStatus}
            orderingPaused={orderingPaused}
            pauseResumesAt={location.online_ordering_paused_until ?? null}
            // Con pausa activa también se corta el agendado: es un cierre general.
            // Modo mesa (QR): sin agendado — el cliente ya está sentado.
            schedulingEnabled={(location.scheduling_enabled ?? false) && !orderingPaused && !tableContext}
            scheduleDays={scheduleDays}
            tableContext={tableContext ?? undefined}
            // Delivery: el estado vive en el padre (sobrevive al cierre del sheet).
            // Acá va el flag CRUDO (sin el `!tableContext`): el sheet aplica ese
            // criterio por su cuenta y es el que le da al cliente la alternativa que
            // reemplaza a los links de marketplace escondidos arriba.
            deliveryEnabled={deliveryEnabled}
            deliveryMode={deliveryMode}
            changeDeliveryMode={changeDeliveryMode}
            dropoff={dropoff}
            setDropoff={setDropoff}
            heldQuote={heldQuote}
            setHeldQuote={setHeldQuote}
            // Envío (Fase 1b T11): mismo criterio que delivery arriba — flag CRUDO,
            // el sheet aplica `!tableContext` por su cuenta (shipModeAvailable).
            shippingEnabled={shippingEnabled}
            shippableItemIds={shippableItemIds}
            shipTo={shipTo}
            setShipTo={setShipTo}
            shipQuote={shipQuote}
            setShipQuote={setShipQuote}
            shipQuoteCartFingerprint={shipQuoteCartFingerprint}
            setShipQuoteCartFingerprint={setShipQuoteCartFingerprint}
            couponsEnabled={couponsEnabled}
            checkoutStepsV2={checkoutStepsV2}
            fulfillmentChosenInBar={fulfillmentChosenInBar}
            // Modo mesa (QR) queda fuera de alcance del plan B (spec §4.2): el
            // flag crudo se apaga acá, no en el sheet, para que no haya que
            // repetir el criterio en cada consumidor de `substitutionsEnabled`.
            substitutionsEnabled={!!substitutionsEnabled && !tableContext}
            menuItems={items}
            welcomePct={welcomePct}
            appliedCoupon={appliedCoupon}
            setAppliedCoupon={setAppliedCoupon}
            pendingCouponCode={pendingCouponCode}
            onPendingCouponConsumed={() => setPendingCouponCode(null)}
            initialCustomer={prefillCustomer}
            notice={prefillNotice}
            fromPrefill={cartFromPrefill}
            onClose={() => {
              setOrderSheetOpen(false)
              // La confirmación de la vuelta se muestra UNA vez: el próximo
              // pedido abre el carrito, no esta orden otra vez.
              setReturnedOrder(null)
            }}
            onWhyFee={() => setWhyFeeOpen(true)}
            initialConfirmedOrder={returnedOrder}
            suggestionSessionId={suggestionTelemetry?.sessionId() ?? null}
            onOrderConfirmed={(orderId) => {
              suggestionTelemetry?.order(orderId)
              // Un pedido cierra la sesión de sugerencias: el siguiente cliente del mismo tab arranca limpio.
              suggestionTelemetry?.endSession()
            }}
          />
        )}
        <AddressSheet
          key="address"
          open={addressSheetOpen}
          locationId={location.id}
          accentColor={accentColor}
          dropoff={dropoff}
          heldQuote={heldQuote}
          onQuote={(q, addr) => {
            if (!shouldAcceptQuote({ deliveryMode: deliveryModeRef.current, addressSheetOpen: addressSheetOpenRef.current })) return
            setHeldQuote(q)
            setDropoff(addr)
          }}
          onClearQuote={() => setHeldQuote(null)}
          onSwitchToPickup={() => {
            changeDeliveryMode('pickup')
            setAddressSheetOpen(false)
          }}
          onClose={() => setAddressSheetOpen(false)}
          onWhyFee={() => setWhyFeeOpen(true)}
        />
      </AnimatePresence>

      <WhyDeliveryFeeSheet
        open={whyFeeOpen}
        accentColor={accentColor}
        foodCents={whyFeeFoodCents}
        deliveryFeeCents={heldQuote?.feeAmountCents ?? null}
        onClose={() => setWhyFeeOpen(false)}
      />

      {showMarketplaceLinks && (
        <DeliveryModal
          open={marketplaceOpen}
          onClose={() => setMarketplaceOpen(false)}
          accentColor={accentColor}
          doordashUrl={doordashUrl ?? undefined}
          uberEatsUrl={uberEatsUrl ?? undefined}
          grubhubUrl={grubhubUrl ?? undefined}
        />
      )}
    </div>
    </MicrositeTheme>
    </LowStockProvider>
  )
}
